import React, { useState } from 'react';
import { GraduationCap, MapPin, DollarSign, Calendar, ExternalLink, Check, X, ChevronRight, ChevronLeft, RefreshCw, Heart, Users, Award } from 'lucide-react';
import './IrelandUniversityMatcher.css';

// Irish Universities Database
const UNIVERSITIES = [
  {
    id: 'tcd',
    name: 'Trinity College Dublin',
    shortName: 'TCD',
    location: 'Dublin',
    ranking: 98,
    worldRanking: 134,
    website: 'https://www.tcd.ie',
    logo: '🎓',
    type: 'Ancient University',
    established: 1592,
    students: 18000,
    courses: ['Computer Science', 'Business', 'Engineering', 'Data Science', 'Healthcare', 'Arts', 'Law'],
    tuitionFees: {
      bachelors: { min: 20000, max: 26000 },
      masters: { min: 22000, max: 28000 },
      phd: { min: 15000, max: 20000 }
    },
    entryRequirements: {
      gpa: { min: 75, recommended: 85 },
      ielts: { min: 6.5, recommended: 7.0 },
      toefl: { min: 88, recommended: 100 },
      pte: { min: 63, recommended: 70 }
    },
    deadlines: {
      fall: 'February 1st',
      spring: 'November 1st'
    },
    features: ['Research-intensive', 'Historic campus', 'Strong industry links', 'Global reputation'],
    accommodation: 'On-campus available',
    scholarships: ['Trinity College Dublin Global Excellence Scholarship', 'Trinity Foundation Scholarships']
  },
  {
    id: 'ucd',
    name: 'University College Dublin',
    shortName: 'UCD',
    location: 'Dublin',
    ranking: 87,
    worldRanking: 181,
    website: 'https://www.ucd.ie',
    logo: '🏛️',
    type: 'Research University',
    established: 1854,
    students: 33000,
    courses: ['Computer Science', 'Business', 'Engineering', 'Data Science', 'Healthcare', 'Arts', 'Law'],
    tuitionFees: {
      bachelors: { min: 18000, max: 24000 },
      masters: { min: 20000, max: 26000 },
      phd: { min: 14000, max: 18000 }
    },
    entryRequirements: {
      gpa: { min: 70, recommended: 80 },
      ielts: { min: 6.5, recommended: 7.0 },
      toefl: { min: 90, recommended: 100 },
      pte: { min: 61, recommended: 68 }
    },
    deadlines: {
      fall: 'March 1st',
      spring: 'October 15th'
    },
    features: ['Ireland\'s largest university', 'Strong business school', 'Modern facilities', 'Active campus life'],
    accommodation: 'On-campus available',
    scholarships: ['UCD Global Excellence Scholarship', 'UCD Smurfit School Scholarships']
  },
  {
    id: 'ul',
    name: 'University of Limerick',
    shortName: 'UL',
    location: 'Limerick',
    ranking: 42,
    worldRanking: 501,
    website: 'https://www.ul.ie',
    logo: '🔬',
    type: 'Technological University',
    established: 1972,
    students: 17000,
    courses: ['Computer Science', 'Business', 'Engineering', 'Data Science', 'Healthcare', 'Arts'],
    tuitionFees: {
      bachelors: { min: 15000, max: 19000 },
      masters: { min: 16000, max: 20000 },
      phd: { min: 12000, max: 16000 }
    },
    entryRequirements: {
      gpa: { min: 60, recommended: 70 },
      ielts: { min: 6.0, recommended: 6.5 },
      toefl: { min: 80, recommended: 90 },
      pte: { min: 55, recommended: 63 }
    },
    deadlines: {
      fall: 'April 1st',
      spring: 'November 1st'
    },
    features: ['Co-op programs', 'Industry partnerships', 'Beautiful campus', 'Strong employability'],
    accommodation: 'On-campus guaranteed for first years',
    scholarships: ['UL Global Scholarship', 'Kemmy Business School Scholarships']
  },
  {
    id: 'dcu',
    name: 'Dublin City University',
    shortName: 'DCU',
    location: 'Dublin',
    ranking: 63,
    worldRanking: 436,
    website: 'https://www.dcu.ie',
    logo: '💡',
    type: 'Technological University',
    established: 1975,
    students: 19000,
    courses: ['Computer Science', 'Business', 'Engineering', 'Data Science', 'Healthcare', 'Arts'],
    tuitionFees: {
      bachelors: { min: 15500, max: 20000 },
      masters: { min: 17000, max: 22000 },
      phd: { min: 13000, max: 17000 }
    },
    entryRequirements: {
      gpa: { min: 65, recommended: 75 },
      ielts: { min: 6.5, recommended: 7.0 },
      toefl: { min: 85, recommended: 95 },
      pte: { min: 59, recommended: 65 }
    },
    deadlines: {
      fall: 'March 1st',
      spring: 'October 15th'
    },
    features: ['Innovation focus', 'Strong tech programs', 'Industry connections', 'Modern campus'],
    accommodation: 'On-campus available',
    scholarships: ['DCU International Scholarship', 'DCU Business School Scholarships']
  },
  {
    id: 'nuig',
    name: 'National University of Ireland Galway',
    shortName: 'NUIG',
    location: 'Galway',
    ranking: 59,
    worldRanking: 287,
    website: 'https://www.universityofgalway.ie',
    logo: '🌊',
    type: 'Research University',
    established: 1845,
    students: 20000,
    courses: ['Computer Science', 'Business', 'Engineering', 'Data Science', 'Healthcare', 'Arts', 'Law'],
    tuitionFees: {
      bachelors: { min: 16000, max: 21000 },
      masters: { min: 17500, max: 23000 },
      phd: { min: 13000, max: 17000 }
    },
    entryRequirements: {
      gpa: { min: 65, recommended: 75 },
      ielts: { min: 6.5, recommended: 7.0 },
      toefl: { min: 88, recommended: 95 },
      pte: { min: 61, recommended: 68 }
    },
    deadlines: {
      fall: 'March 1st',
      spring: 'October 15th'
    },
    features: ['Research excellence', 'Cultural hub', 'Medical school', 'Vibrant city life'],
    accommodation: 'On-campus available',
    scholarships: ['NUIG International Scholarship', 'Hardiman Scholarship']
  },
  {
    id: 'ucc',
    name: 'University College Cork',
    shortName: 'UCC',
    location: 'Cork',
    ranking: 55,
    worldRanking: 292,
    website: 'https://www.ucc.ie',
    logo: '🏰',
    type: 'Research University',
    established: 1845,
    students: 22000,
    courses: ['Computer Science', 'Business', 'Engineering', 'Data Science', 'Healthcare', 'Arts', 'Law'],
    tuitionFees: {
      bachelors: { min: 17000, max: 22000 },
      masters: { min: 18500, max: 24000 },
      phd: { min: 14000, max: 18000 }
    },
    entryRequirements: {
      gpa: { min: 70, recommended: 80 },
      ielts: { min: 6.5, recommended: 7.0 },
      toefl: { min: 90, recommended: 100 },
      pte: { min: 63, recommended: 70 }
    },
    deadlines: {
      fall: 'March 1st',
      spring: 'October 15th'
    },
    features: ['Green campus', 'Research focus', 'Food science excellence', 'Historic city'],
    accommodation: 'On-campus available',
    scholarships: ['UCC International Scholarship', 'Quercus Talented Students Programme']
  },
  {
    id: 'tudublin',
    name: 'Technological University Dublin',
    shortName: 'TU Dublin',
    location: 'Dublin',
    ranking: 35,
    worldRanking: 801,
    website: 'https://www.tudublin.ie',
    logo: '⚙️',
    type: 'Technological University',
    established: 2019,
    students: 30000,
    courses: ['Computer Science', 'Business', 'Engineering', 'Data Science', 'Healthcare', 'Arts'],
    tuitionFees: {
      bachelors: { min: 12000, max: 16000 },
      masters: { min: 14000, max: 18000 },
      phd: { min: 10000, max: 14000 }
    },
    entryRequirements: {
      gpa: { min: 60, recommended: 70 },
      ielts: { min: 6.0, recommended: 6.5 },
      toefl: { min: 75, recommended: 85 },
      pte: { min: 50, recommended: 59 }
    },
    deadlines: {
      fall: 'April 15th',
      spring: 'November 15th'
    },
    features: ['Practical focus', 'Industry partnerships', 'Multiple campuses', 'Affordable fees'],
    accommodation: 'Limited on-campus',
    scholarships: ['TU Dublin International Scholarship', 'TU Dublin Merit Scholarship']
  }
];

const COURSE_OPTIONS = [
  { id: 'computer-science', name: 'Computer Science', icon: '💻' },
  { id: 'business', name: 'Business', icon: '📊' },
  { id: 'engineering', name: 'Engineering', icon: '🔧' },
  { id: 'data-science', name: 'Data Science', icon: '📈' },
  { id: 'healthcare', name: 'Healthcare', icon: '🏥' },
  { id: 'arts', name: 'Arts', icon: '🎨' },
  { id: 'law', name: 'Law', icon: '⚖️' }
];

const STUDY_LEVELS = [
  { id: 'bachelors', name: "Bachelor's", duration: '3-4 years' },
  { id: 'masters', name: "Master's", duration: '1-2 years' },
  { id: 'phd', name: 'PhD', duration: '3-4 years' }
];

const ENGLISH_TESTS = [
  { id: 'ielts', name: 'IELTS', min: 5.5, max: 8.0, step: 0.5 },
  { id: 'toefl', name: 'TOEFL', min: 70, max: 100, step: 1 },
  { id: 'pte', name: 'PTE', min: 50, max: 80, step: 1 }
];

const IrelandUniversityMatcher = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    gpa: 70,
    englishTest: 'ielts',
    englishScore: 6.5,
    course: '',
    budget: 20000,
    studyLevel: 'masters',
    locationPreference: ''
  });
  const [results, setResults] = useState(null);
  const [compareList, setCompareList] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [favorites, setFavorites] = useState([]);

  const totalSteps = 4;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateMatch = (university) => {
    let score = 0;
    const reasons = [];
    const warnings = [];

    // GPA Match (30 points)
    const gpaScore = Math.min(30, Math.max(0, ((formData.gpa - university.entryRequirements.gpa.min) / 
      (university.entryRequirements.gpa.recommended - university.entryRequirements.gpa.min)) * 30));
    score += gpaScore;
    
    if (formData.gpa >= university.entryRequirements.gpa.recommended) {
      reasons.push('GPA exceeds recommended requirements');
    } else if (formData.gpa >= university.entryRequirements.gpa.min) {
      reasons.push('GPA meets minimum requirements');
    } else {
      warnings.push(`GPA below minimum requirement (${university.entryRequirements.gpa.min}%)`);
    }

    // English Score Match (25 points)
    const englishReq = university.entryRequirements[formData.englishTest];
    const englishScorePercent = (formData.englishScore - englishReq.min) / 
      (englishReq.recommended - englishReq.min);
    const englishPoints = Math.min(25, Math.max(0, englishScorePercent * 25));
    score += englishPoints;

    if (formData.englishScore >= englishReq.recommended) {
      reasons.push(`${formData.englishTest.toUpperCase()} score exceeds recommended`);
    } else if (formData.englishScore >= englishReq.min) {
      reasons.push(`${formData.englishTest.toUpperCase()} score meets minimum`);
    } else {
      warnings.push(`${formData.englishTest.toUpperCase()} below minimum (${englishReq.min})`);
    }

    // Course Availability (20 points)
    const courseName = COURSE_OPTIONS.find(c => c.id === formData.course)?.name || '';
    if (university.courses.includes(courseName)) {
      score += 20;
      reasons.push(`${courseName} program available`);
    } else {
      warnings.push(`${courseName} program not offered`);
    }

    // Budget Match (15 points)
    const fees = university.tuitionFees[formData.studyLevel];
    if (formData.budget >= fees.max) {
      score += 15;
      reasons.push('Budget covers all tuition costs');
    } else if (formData.budget >= fees.min) {
      score += 10;
      reasons.push('Budget covers minimum tuition');
    } else {
      warnings.push(`Tuition may exceed budget (€${fees.min.toLocaleString()} - €${fees.max.toLocaleString()})`);
    }

    // Location Preference (10 points)
    if (!formData.locationPreference || university.location === formData.locationPreference) {
      score += 10;
      if (formData.locationPreference) {
        reasons.push(`Located in preferred city (${university.location})`);
      }
    }

    return {
      score: Math.round(score),
      reasons,
      warnings,
      fees: university.tuitionFees[formData.studyLevel]
    };
  };

  const handleFindMatches = () => {
    const matchedUniversities = UNIVERSITIES.map(uni => ({
      ...uni,
      match: calculateMatch(uni)
    })).sort((a, b) => b.match.score - a.match.score);

    setResults(matchedUniversities);
    setCurrentStep(5);
  };

  const toggleCompare = (universityId) => {
    if (compareList.includes(universityId)) {
      setCompareList(compareList.filter(id => id !== universityId));
    } else if (compareList.length < 3) {
      setCompareList([...compareList, universityId]);
    }
  };

  const toggleFavorite = (universityId) => {
    if (favorites.includes(universityId)) {
      setFavorites(favorites.filter(id => id !== universityId));
    } else {
      setFavorites([...favorites, universityId]);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setFormData({
      gpa: 70,
      englishTest: 'ielts',
      englishScore: 6.5,
      course: '',
      budget: 20000,
      studyLevel: 'masters',
      locationPreference: ''
    });
    setResults(null);
    setCompareList([]);
    setShowComparison(false);
  };

  const getEnglishTestConfig = () => ENGLISH_TESTS.find(t => t.id === formData.englishTest);

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {[1, 2, 3, 4].map((step) => (
        <div 
          key={step} 
          className={`step-dot ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
        >
          <span className="step-number">{step}</span>
          <span className="step-label">
            {step === 1 && 'Academics'}
            {step === 2 && 'Course'}
            {step === 3 && 'Budget'}
            {step === 4 && 'Preferences'}
          </span>
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="form-step">
      <h3>Academic Profile</h3>
      <p className="step-description">Tell us about your academic background</p>
      
      <div className="form-group">
        <label>
          <GraduationCap size={20} />
          GPA / Percentage
        </label>
        <div className="slider-container">
          <input
            type="range"
            min="60"
            max="95"
            value={formData.gpa}
            onChange={(e) => handleInputChange('gpa', parseInt(e.target.value))}
            className="slider"
          />
          <div className="slider-value">{formData.gpa}%</div>
        </div>
        <div className="slider-labels">
          <span>60%</span>
          <span>95%</span>
        </div>
      </div>

      <div className="form-group">
        <label>English Proficiency Test</label>
        <div className="test-selector">
          {ENGLISH_TESTS.map(test => (
            <button
              key={test.id}
              className={`test-btn ${formData.englishTest === test.id ? 'active' : ''}`}
              aria-pressed={formData.englishTest === test.id}
              onClick={() => {
                handleInputChange('englishTest', test.id);
                handleInputChange('englishScore', test.min);
              }}
            >
              {test.name}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>{getEnglishTestConfig()?.name} Score</label>
        <div className="slider-container">
          <input
            type="range"
            min={getEnglishTestConfig()?.min}
            max={getEnglishTestConfig()?.max}
            step={getEnglishTestConfig()?.step}
            value={formData.englishScore}
            onChange={(e) => handleInputChange('englishScore', parseFloat(e.target.value))}
            className="slider"
          />
          <div className="slider-value">{formData.englishScore}</div>
        </div>
        <div className="slider-labels">
          <span>{getEnglishTestConfig()?.min}</span>
          <span>{getEnglishTestConfig()?.max}</span>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="form-step">
      <h3>Course Selection</h3>
      <p className="step-description">What would you like to study?</p>
      
      <div className="form-group">
        <label>Study Level</label>
        <div className="level-selector">
          {STUDY_LEVELS.map(level => (
            <button
              key={level.id}
              className={`level-btn ${formData.studyLevel === level.id ? 'active' : ''}`}
              aria-pressed={formData.studyLevel === level.id}
              onClick={() => handleInputChange('studyLevel', level.id)}
            >
              <span className="level-name">{level.name}</span>
              <span className="level-duration">{level.duration}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Course Preference</label>
        <div className="course-grid">
          {COURSE_OPTIONS.map(course => (
            <button
              key={course.id}
              className={`course-btn ${formData.course === course.id ? 'active' : ''}`}
              aria-pressed={formData.course === course.id}
              onClick={() => handleInputChange('course', course.id)}
            >
              <span className="course-icon">{course.icon}</span>
              <span className="course-name">{course.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-step">
      <h3>Budget Planning</h3>
      <p className="step-description">What's your annual tuition budget?</p>
      
      <div className="form-group">
        <label>
          <DollarSign size={20} />
          Annual Tuition Budget (EUR)
        </label>
        <div className="slider-container budget-slider">
          <input
            type="range"
            min="15000"
            max="35000"
            step="1000"
            value={formData.budget}
            onChange={(e) => handleInputChange('budget', parseInt(e.target.value))}
            className="slider"
          />
          <div className="slider-value">€{formData.budget.toLocaleString()}</div>
        </div>
        <div className="slider-labels">
          <span>€15,000</span>
          <span>€35,000</span>
        </div>
      </div>

      <div className="budget-info">
        <div className="info-card">
          <h4>💡 Budget Tips</h4>
          <ul>
            <li>TU Dublin and UL typically have lower tuition fees</li>
            <li>TCD and UCD are premium institutions with higher fees</li>
            <li>Scholarships can reduce costs by €5,000-€10,000</li>
            <li>Living costs vary by city (Dublin is most expensive)</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="form-step">
      <h3>Location Preference</h3>
      <p className="step-description">Where would you prefer to study?</p>
      
      <div className="form-group">
        <label>
          <MapPin size={20} />
          Preferred City (Optional)
        </label>
        <div className="location-grid">
          {['Dublin', 'Cork', 'Galway', 'Limerick'].map(city => (
            <button
              key={city}
              className={`location-btn ${formData.locationPreference === city ? 'active' : ''}`}
              aria-pressed={formData.locationPreference === city}
              onClick={() => handleInputChange('locationPreference', 
                formData.locationPreference === city ? '' : city)}
            >
              <MapPin size={18} />
              <span>{city}</span>
            </button>
          ))}
        </div>
        <p className="helper-text">Leave unselected to see all universities</p>
      </div>

      <div className="summary-card">
        <h4>📋 Your Profile Summary</h4>
        <div className="summary-items">
          <div className="summary-item">
            <span className="summary-label">GPA</span>
            <span className="summary-value">{formData.gpa}%</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">English Test</span>
            <span className="summary-value">{formData.englishTest.toUpperCase()} {formData.englishScore}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Study Level</span>
            <span className="summary-value">{STUDY_LEVELS.find(l => l.id === formData.studyLevel)?.name}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Course</span>
            <span className="summary-value">{COURSE_OPTIONS.find(c => c.id === formData.course)?.name || 'Not selected'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Budget</span>
            <span className="summary-value">€{formData.budget.toLocaleString()}/year</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Location</span>
            <span className="summary-value">{formData.locationPreference || 'Any'}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderResults = () => {
    if (!results) return null;

    return (
      <div className="results-container">
        <div className="results-header">
          <h3>🎯 Your University Matches</h3>
          <div className="results-actions">
            {compareList.length >= 2 && (
              <button 
                className="compare-btn"
                onClick={() => setShowComparison(!showComparison)}
              >
                Compare ({compareList.length})
              </button>
            )}
            <button className="reset-btn" onClick={handleReset}>
              <RefreshCw size={18} />
              Start Over
            </button>
          </div>
        </div>

        {showComparison && (
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-cell header-cell">Feature</div>
              {compareList.map(id => {
                const uni = UNIVERSITIES.find(u => u.id === id);
                return (
                  <div key={id} className="comparison-cell header-cell">
                    <span className="uni-logo">{uni?.logo}</span>
                    <span>{uni?.shortName}</span>
                  </div>
                );
              })}
            </div>
            {['ranking', 'location', 'tuitionFees', 'entryRequirements', 'deadlines'].map(feature => (
              <div key={feature} className="comparison-row">
                <div className="comparison-cell label-cell">
                  {feature.charAt(0).toUpperCase() + feature.slice(1).replace(/([A-Z])/g, ' $1')}
                </div>
                {compareList.map(id => {
                  const uni = UNIVERSITIES.find(u => u.id === id);
                  return (
                    <div key={id} className="comparison-cell">
                      {feature === 'tuitionFees' && (
                        <span>€{uni?.tuitionFees[formData.studyLevel]?.min?.toLocaleString()} - €{uni?.tuitionFees[formData.studyLevel]?.max?.toLocaleString()}</span>
                      )}
                      {feature === 'ranking' && <span>#{uni?.ranking} in Ireland</span>}
                      {feature === 'location' && <span>{uni?.location}</span>}
                      {feature === 'entryRequirements' && (
                        <span>GPA: {uni?.entryRequirements.gpa.min}%+, IELTS: {uni?.entryRequirements.ielts.min}+</span>
                      )}
                      {feature === 'deadlines' && <span>Fall: {uni?.deadlines.fall}</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <div className="university-grid">
          {results.map((university, index) => (
            <div 
              key={university.id} 
              className={`university-card ${index === 0 ? 'top-match' : ''}`}
            >
              {index === 0 && <div className="top-match-badge">🏆 Top Match</div>}
              
              <div className="card-header">
                <div className="uni-info">
                  <span className="uni-logo">{university.logo}</span>
                  <div className="uni-details">
                    <h4>{university.name}</h4>
                    <div className="uni-meta">
                      <span><MapPin size={14} /> {university.location}</span>
                      <span><Award size={14} /> #{university.ranking} in Ireland</span>
                    </div>
                  </div>
                </div>
                <div className="match-score">
                  <div 
                    className="score-circle"
                    style={{ 
                      background: `conic-gradient(
                        ${university.match.score >= 80 ? '#10b981' : 
                          university.match.score >= 60 ? '#f59e0b' : '#ef4444'} 
                        ${university.match.score * 3.6}deg, 
                        #2d3748 0deg)`
                    }}
                  >
                    <span>{university.match.score}%</span>
                  </div>
                  <span className="match-label">Match</span>
                </div>
              </div>

              <div className="card-body">
                <div className="fees-row">
                  <span className="fees-label">Tuition ({STUDY_LEVELS.find(l => l.id === formData.studyLevel)?.name})</span>
                  <span className="fees-value">
                    €{university.match.fees.min.toLocaleString()} - €{university.match.fees.max.toLocaleString()}
                  </span>
                </div>

                <div className="requirements-section">
                  <h5>Entry Requirements</h5>
                  <div className="req-items">
                    <div className={`req-item ${formData.gpa >= university.entryRequirements.gpa.min ? 'met' : 'unmet'}`}>
                      {formData.gpa >= university.entryRequirements.gpa.min ? <Check size={14} /> : <X size={14} />}
                      <span>GPA: {university.entryRequirements.gpa.min}%+ (You: {formData.gpa}%)</span>
                    </div>
                    <div className={`req-item ${formData.englishScore >= university.entryRequirements[formData.englishTest].min ? 'met' : 'unmet'}`}>
                      {formData.englishScore >= university.entryRequirements[formData.englishTest].min ? <Check size={14} /> : <X size={14} />}
                      <span>{formData.englishTest.toUpperCase()}: {university.entryRequirements[formData.englishTest].min}+ (You: {formData.englishScore})</span>
                    </div>
                  </div>
                </div>

                {university.match.reasons.length > 0 && (
                  <div className="reasons-list">
                    {university.match.reasons.slice(0, 2).map((reason, i) => (
                      <div key={i} className="reason-item positive">
                        <Check size={12} /> {reason}
                      </div>
                    ))}
                  </div>
                )}

                {university.match.warnings.length > 0 && (
                  <div className="warnings-list">
                    {university.match.warnings.slice(0, 2).map((warning, i) => (
                      <div key={i} className="warning-item">
                        <X size={12} /> {warning}
                      </div>
                    ))}
                  </div>
                )}

                <div className="deadline-row">
                  <Calendar size={14} />
                  <span>Application Deadline (Fall): {university.deadlines.fall}</span>
                </div>
              </div>

              <div className="card-footer">
                <a 
                  href={university.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="website-link"
                >
                  Visit Website <ExternalLink size={14} />
                </a>
                <div className="action-buttons">
                  <button 
                    className={`action-btn favorite ${favorites.includes(university.id) ? 'active' : ''}`}
                    onClick={() => toggleFavorite(university.id)}
                    title="Add to favorites"
                  >
                    <Heart size={16} fill={favorites.includes(university.id) ? 'currentColor' : 'none'} />
                  </button>
                  <button 
                    className={`action-btn compare ${compareList.includes(university.id) ? 'active' : ''}`}
                    onClick={() => toggleCompare(university.id)}
                    disabled={!compareList.includes(university.id) && compareList.length >= 3}
                    title="Add to compare"
                  >
                    <Users size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.gpa >= 60 && formData.englishScore >= getEnglishTestConfig()?.min;
      case 2:
        return formData.course !== '';
      case 3:
        return formData.budget >= 15000;
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="university-matcher">
      <div className="matcher-header">
        <h2>🎓 Ireland University Matcher</h2>
        <p className="matcher-subtitle">Shortlist Irish universities based on your academic profile, course preferences, and budget</p>
        <p className="matcher-disclaimer">Results are a planning shortlist, not an admission decision. Verify current entry requirements, fees, deadlines, and course availability with each university.</p>
      </div>

      {currentStep <= totalSteps && renderStepIndicator()}

      <div className="matcher-content">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderResults()}
      </div>

      {currentStep <= totalSteps && (
        <div className="matcher-footer">
          {currentStep > 1 && (
            <button 
              className="nav-btn back"
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              <ChevronLeft size={18} /> Back
            </button>
          )}
          {currentStep < totalSteps ? (
            <button 
              className="nav-btn next"
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
            >
              Next <ChevronRight size={18} />
            </button>
          ) : (
            <button 
              className="nav-btn find"
              onClick={handleFindMatches}
              disabled={!canProceed()}
            >
              Find My Matches 🎯
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default IrelandUniversityMatcher;
